import React, { useRef, useState } from 'react';

import { reportException } from '@origin-digital/reporting-client';
import { Recaptcha, ReCAPTCHA, RecaptchaDisclaimer } from '@origin-digital/recaptcha';
import { Card, Stack, Heading, Button, TextField } from '@origin-digital/ods-core';
import { required, validEmail } from '@origin-digital/validation';

import { config } from 'src/config';
import { divisionIdLabelMap } from 'src/consts';
import { useTextFieldState } from 'src/hooks';
import { emailReceiptsPost } from 'src/services';
import { reduceValidators, isMyAccount } from 'src/utils/helpers';
import {
  hideNotification,
  showErrorNotification,
  showSuccessNotification,
} from 'src/utils/notification';
import { Form } from 'src/components/Form';

import { IProps } from './types';
import * as content from './content.json';

export const EmailReceipt: React.SFC<IProps> = ({ context }) => {
  const emailState = useTextFieldState('');
  const [isSendingReceipt, setIsSendingReceipt] = useState<boolean>(false);
  const recaptchaRef: React.MutableRefObject<ReCAPTCHA> = useRef<ReCAPTCHA>();

  const handleValidate = (): string => {
    const { errors } = content;

    const validators = [
      required({ error: errors.required }),
      validEmail({ error: errors.invalid }),
    ];

    const errorMessage = reduceValidators(emailState.value, validators);

    emailState.setError(errorMessage);

    return errorMessage;
  };

  const handleSubmit = async (recaptcha?: string) => {
    const { contractAccountId, divisionId, receiptNumber } = context;

    hideNotification();

    try {
      const referenceNumber = receiptNumber.replace(/\s/g, '');
      const accountNumber = contractAccountId.replace(/\s/g, '');
      await emailReceiptsPost({
        accountNumber,
        emailAddress: emailState.value,
        energyType: divisionIdLabelMap[divisionId],
        referenceNumber,
        recaptcha,
      });

      showSuccessNotification(content.notifications.success);
      if (recaptchaRef.current && recaptchaRef.current.getValue()) {
        recaptchaRef.current.reset();
      }
    } catch (error) {
      showErrorNotification(content.notifications.error);
      reportException('emailReceipt.handleSubmit()', error);
    } finally {
      setIsSendingReceipt(false);
    }
  };

  const verifyRecaptcha = () => {
    if (handleValidate()) {
      return;
    }

    setIsSendingReceipt(true);
    hideNotification();

    if (isMyAccount()) {
      handleSubmit();
    } else {
      if (recaptchaRef.current && recaptchaRef.current.getValue()) {
        recaptchaRef.current.reset();
      }
      recaptchaRef.current.execute();
    }
  };

  return (
    <Card data-id="email-receipt">
      <Form onSubmit={verifyRecaptcha}>
        <Stack space={['large', 'xlarge']}>
          <Heading variant="h4">{content.heading}</Heading>
          <TextField
            data-id="email-address"
            id="email-address"
            name="email-address"
            type="email"
            label={content.email}
            onBlur={handleValidate}
            onChange={emailState.setValue}
            helperText={emailState.helperText}
            value={emailState.value}
            error={emailState.error}
            disabled={isSendingReceipt}
          />

          <Button data-id="send-email-receipt" spinner={isSendingReceipt} type="submit">
            {content.sendButton}
          </Button>

          {!isMyAccount() && (
            <>
              <Recaptcha
                onClose={() => setIsSendingReceipt(false)}
                onSuccess={handleSubmit}
                recaptchaRef={recaptchaRef}
                recaptchaKey={config.recaptchaKey}
              />
              <RecaptchaDisclaimer />
            </>
          )}
        </Stack>
      </Form>
    </Card>
  );
};
